import { useEffect, useState } from "react";
import type { JSX } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { Mode } from "./types";

const ACCENT: Record<Mode, string> = { roast: "#E62D23", toast: "#5382FF" };

/** Format seconds as M:SS. NaN/negative -> "0:00". */
export function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const total = Math.floor(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function PlayerControls({
  isPlaying,
  currentTime,
  duration,
  mode,
  onToggle,
  onSeek,
  fullscreen = false,
  onToggleFullscreen,
}: {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  mode: Mode;
  onToggle: () => void;
  onSeek: (t: number) => void;
  /** Whether the player is in (theater/native) fullscreen — flips the icon/label. */
  fullscreen?: boolean;
  /** When provided, a fullscreen toggle renders at the right end of the bar. */
  onToggleFullscreen?: () => void;
}): JSX.Element {
  const accent = ACCENT[mode];
  const reduceMotion = useReducedMotion();
  // Draw the eye to the (small) play button until the roast has been played once — testers
  // were missing it and tapping the export button instead. One-time onboarding nudge: it
  // pulses while never-yet-played + paused, and stops for good after the first play (it does
  // NOT resume if they pause again). Honors prefers-reduced-motion.
  const [hasEverPlayed, setHasEverPlayed] = useState(false);
  useEffect(() => { if (isPlaying) setHasEverPlayed(true); }, [isPlaying]);
  const nudge = !hasEverPlayed && !isPlaying && !reduceMotion;
  return (
    <div
      data-testid="player-controls"
      className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-4 py-2"
      style={{ background: "linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0))" }}
    >
      <motion.button
        type="button"
        aria-label={isPlaying ? "Pause" : "Play"}
        data-testid="play-toggle"
        data-nudging={nudge ? "true" : "false"}
        onClick={onToggle}
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white"
        style={{ backgroundColor: accent }}
        animate={nudge ? { scale: [1, 1.18, 1] } : { scale: 1 }}
        transition={
          nudge
            ? { duration: 0.7, repeat: Infinity, repeatDelay: 1.1, ease: "easeInOut" }
            : { duration: 0.2 }
        }
      >
        {isPlaying ? (
          <svg width="14" height="14" viewBox="0 0 12 12" aria-hidden="true">
            <rect x="2" y="1.5" width="2.6" height="9" fill="currentColor" />
            <rect x="7.4" y="1.5" width="2.6" height="9" fill="currentColor" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M3 1.5 L10 6 L3 10.5 Z" fill="currentColor" />
          </svg>
        )}
      </motion.button>
      <input
        data-testid="seek"
        type="range"
        min={0}
        max={Number.isFinite(duration) ? duration : 0}
        step={0.01}
        value={currentTime}
        onChange={(e) => onSeek(Number(e.target.value))}
        className="h-1 flex-1 cursor-pointer appearance-none rounded-full"
        style={{ accentColor: accent }}
        aria-label="Seek"
      />
      <span data-testid="end-time" className="shrink-0 text-xs font-medium tabular-nums text-white">
        {formatTime(duration)}
      </span>
      {onToggleFullscreen && (
        <button
          type="button"
          aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          onClick={onToggleFullscreen}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white/90 hover:bg-white/10 hover:text-white"
        >
          {fullscreen ? (
            <svg width="15" height="15" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 1v4H1M9 1v4h4M5 13V9H1M9 13V9h4" />
            </svg>
          ) : (
            <svg width="15" height="15" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M1 5V1h4M13 5V1H9M1 9v4h4M13 9v4H9" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}
